import React, { useMemo, useState } from 'react';
import { Send, Mail, MessageCircle, Smartphone, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { useApiQuery } from '../../hooks/useApi';
import { notificationService } from '../../services/api';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormSection from '../../components/Common/FormSection';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { RichTextHtml } from '../../components/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const CHANNEL_OPTIONS = [
  { id: 'email', label: 'E-mail', icon: Mail },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { id: 'sms', label: 'SMS', icon: Smartphone },
];

const DEFAULT_VARS = `{
  "nome": "",
  "empresa": "",
  "licenca_numero": "",
  "data": ""
}`;

const SendNotification = () => {
  const { t } = useTranslation('notifications');

  const [form, setForm] = useState({
    notification_type_id: '',
    template_id: '',
    channels: ['email'],
    to_email: '',
    to_phone_e164: '',
    variablesText: DEFAULT_VARS,
  });
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const { data: typesRaw } = useApiQuery('notification-types-send', async () => {
    const r = await notificationService.listTypes();
    return r.data;
  });
  const types = Array.isArray(typesRaw) ? typesRaw : typesRaw?.data ?? [];

  const selectedType = types.find((t) => t.id === form.notification_type_id);

  const { data: templatesRaw } = useApiQuery(
    ['notification-templates-send', form.notification_type_id],
    async () => {
      if (!form.notification_type_id) return [];
      const r = await notificationService.listTemplates({
        notification_type_id: form.notification_type_id,
      });
      return r.data;
    },
    { enabled: !!form.notification_type_id }
  );
  const templates = Array.isArray(templatesRaw) ? templatesRaw : templatesRaw?.data ?? [];

  const templatesForChannel = useMemo(() => {
    const ch = form.channels[0];
    if (!ch) return templates;
    return templates.filter((t) => t.channel === ch);
  }, [templates, form.channels]);

  const { data: channelStatus } = useApiQuery('notification-channel-status', async () => {
    const r = await notificationService.getStatus();
    return r.data;
  });

  const parseVariables = () => {
    try {
      return JSON.parse(form.variablesText || '{}');
    } catch {
      return null;
    }
  };

  const toggleChannel = (ch) => {
    setForm((f) => {
      const set = new Set(f.channels);
      if (set.has(ch)) set.delete(ch);
      else set.add(ch);
      return { ...f, channels: Array.from(set), template_id: '' };
    });
  };

  const handlePreview = async () => {
    const variables = parseVariables();
    if (variables === null) {
      toast.error(t('invalidVariables'));
      return;
    }
    const tpl = templates.find((t) => t.id === form.template_id);
    if (!tpl) {
      toast.error(t('selectTemplatePreview'));
      return;
    }
    try {
      const r = await notificationService.previewTemplate({
        subject: tpl.subject,
        body_html: tpl.body_html,
        body_text: tpl.body_text,
        variables,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(userFacingError(e, 'previewError'));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const variables = parseVariables();
    if (variables === null) {
      toast.error(t('invalidVariables'));
      return;
    }
    if (!form.notification_type_id && !form.template_id) {
      toast.error(t('selectTypeOrTemplate'));
      return;
    }
    if (!form.channels.length) {
      toast.error(t('selectChannel'));
      return;
    }
    if (form.channels.includes('email') && !form.to_email.trim()) {
      toast.error(t('recipientEmailRequired'));
      return;
    }
    if (form.channels.some((c) => c === 'whatsapp' || c === 'sms') && !form.to_phone_e164.trim()) {
      toast.error(t('phoneRequired'));
      return;
    }

    const typeCode = selectedType?.code;
    setSending(true);
    setLastResult(null);
    try {
      const r = await notificationService.send({
        notification_type_code: typeCode || undefined,
        notification_type_id: form.notification_type_id || undefined,
        template_id: form.template_id || undefined,
        channels: form.channels,
        to_email: form.to_email.trim() || undefined,
        to_phone_e164: form.to_phone_e164.trim() || undefined,
        variables,
      });
      setLastResult(r.data);
      const sent = r.data?.sent_count ?? 0;
      if (sent > 0) toast.success(`Enviado em ${sent} canal(is)`);
      else toast.error(t('noChannelSent'));
    } catch (err) {
      toast.error(userFacingError(err, 'sendError'));
    } finally {
      setSending(false);
    }
  };

  const emailOk = channelStatus?.channels?.email?.configured;
  const whatsappOk = channelStatus?.channels?.whatsapp?.configured;
  const smsOk = channelStatus?.channels?.sms?.configured;

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t("sendTitle")}
          description="Dispare mensagens por e-mail, WhatsApp e SMS usando templates configurados por tipo de notificação."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Notificações', to: '/notifications' },
            { label: 'Enviar' },
          ]}
        />

        <div className="flex flex-wrap gap-3 text-sm">
          <span className={`rounded-full px-3 py-1 ${emailOk ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            E-mail: {emailOk ? 'configurado' : 'não configurado'}
          </span>
          <span className={`rounded-full px-3 py-1 ${whatsappOk ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            WhatsApp: {whatsappOk ? 'configurado' : 'não configurado'}
          </span>
          <span className={`rounded-full px-3 py-1 ${smsOk ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
            SMS: {smsOk ? 'configurado' : 'não configurado'}
          </span>
        </div>

        <form onSubmit={handleSend} className="space-y-6">
          <FormSection
            id="send-sec-tipo"
            title={t("typeAndTemplate")}
            description="O template padrão do tipo será usado se nenhum template específico for escolhido."
            icon={Send}
            variant="primary"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="form-group">
                <label className="form-label form-label-required">Tipo de notificação</label>
                <select
                  className="form-select"
                  value={form.notification_type_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      notification_type_id: e.target.value,
                      template_id: '',
                    }))
                  }
                  required
                >
                  <option value="">Selecione…</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {selectedType?.description ? (
                  <p className="mt-1 text-xs text-gray-500">{selectedType.description}</p>
                ) : null}
              </div>
              <div className="form-group">
                <label className="form-label">Template (opcional)</label>
                <select
                  className="form-select"
                  value={form.template_id}
                  onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
                  disabled={!form.notification_type_id}
                >
                  <option value="">Padrão do tipo/canal</option>
                  {templatesForChannel.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.channel}){t.is_default ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection
            id="send-sec-canais"
            title={t("channels")}
            description="Marque os canais que deseja utilizar neste disparo."
            icon={Mail}
            variant="slate"
          >
            <div className="flex flex-wrap gap-4">
              {CHANNEL_OPTIONS.map(({ id, label, icon: Icon }) => (
                <label key={id} className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-4 py-2">
                  <input
                    type="checkbox"
                    checked={form.channels.includes(id)}
                    onChange={() => toggleChannel(id)}
                  />
                  <Icon className="h-4 w-4 text-primary-600" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </FormSection>

          <FormSection
            id="send-sec-dest"
            title={t("recipients")}
            description="Preencha os contatos conforme os canais selecionados."
            icon={MessageCircle}
            variant="emerald"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {form.channels.includes('email') ? (
                <div className="form-group">
                  <label className="form-label form-label-required">E-mail</label>
                  <input
                    type="email"
                    className="form-input"
                    value={form.to_email}
                    onChange={(e) => setForm((f) => ({ ...f, to_email: e.target.value }))}
                    placeholder="destinatario@empresa.com"
                  />
                </div>
              ) : null}
              {form.channels.some((channel) => channel === 'whatsapp' || channel === 'sms') ? (
                <div className="form-group">
                  <label className="form-label form-label-required">Telefone (E.164)</label>
                  <input
                    className="form-input"
                    value={form.to_phone_e164}
                    onChange={(e) => setForm((f) => ({ ...f, to_phone_e164: e.target.value }))}
                    placeholder="+5511999999999"
                  />
                </div>
              ) : null}
            </div>
          </FormSection>

          <FormSection
            id="send-sec-vars"
            title={t("variables")}
            description="Valores substituídos nos placeholders {{variavel}} do template."
            icon={Send}
            variant="primary"
          >
            <textarea
              className="form-input min-h-[140px] font-mono text-sm"
              value={form.variablesText}
              onChange={(e) => setForm((f) => ({ ...f, variablesText: e.target.value }))}
            />
          </FormSection>

          <div className="flex flex-wrap gap-3">
            <Can permission={P.NOTIFICATIONS_MANAGE}>
              <button type="submit" className="btn btn-primary inline-flex items-center gap-2" disabled={sending}>
                <Send className="h-4 w-4" />
                {sending ? 'Enviando…' : 'Enviar notificação'}
              </button>
            </Can>
            <button type="button" className="btn btn-outline inline-flex items-center gap-2" onClick={handlePreview}>
              <Eye className="h-4 w-4" />
              Pré-visualizar template
            </button>
          </div>
        </form>

        {preview ? (
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900">Pré-visualização</h3>
            {preview.subject ? <p className="mt-2 text-sm"><strong>Assunto:</strong> {preview.subject}</p> : null}
            {preview.body_html ? (
              <div className="mt-3 rounded border border-gray-100 p-3">
                <RichTextHtml html={preview.body_html} />
              </div>
            ) : null}
            {preview.body_text ? (
              <pre className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{preview.body_text}</pre>
            ) : null}
          </div>
        ) : null}

        {lastResult ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
            <p className="font-medium text-gray-900">Resultado do envio</p>
            <ul className="mt-2 space-y-1">
              {(lastResult.results || []).map((r, i) => (
                <li key={i} className="text-gray-700">
                  <strong>{r.channel}:</strong> {r.status}
                  {r.to ? ` → ${r.to}` : ''}
                  {r.detail ? ` — ${r.detail}` : ''}
                  {r.reason ? ` — ${r.reason}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </FormPageShell>
  );
};

export default SendNotification;
